import { useState, useEffect, useCallback } from 'react';

interface ResponsiveMapConfig {
  containerHeight: number;
  zoom: number;
  center: [number, number];
  layoutSize: string;
  layoutCenter: [string, string];
  fontSize: number;
  borderWidth: number;
}

interface ContainerSize {
  width: number;
  height: number;
}

// 防抖函数
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const useResponsiveMap = (containerSize?: ContainerSize): ResponsiveMapConfig => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080
  });

  // 使用容器尺寸或窗口尺寸
  const effectiveSize = containerSize && containerSize.width > 0 ? containerSize : dimensions;

  // 计算响应式容器高度 - 设为屏幕高度的90%
  const calculateContainerHeight = useCallback(() => {
    const { width, height } = effectiveSize;
    
    // 容器高度设为屏幕高度的90%，最大化利用垂直空间
    const baseHeight = height * 0.9;
    
    // 根据屏幕尺寸设置最小和最大限制
    let minHeight, maxHeight;
    
    if (width >= 2560) {
      // 4K屏幕
      minHeight = 1200;
      maxHeight = 1800;
    } else if (width >= 1920) {
      // 1080p屏幕
      minHeight = 900;
      maxHeight = 1500;
    } else if (width >= 1680) {
      // 1050p屏幕
      minHeight = 800;
      maxHeight = 1300;
    } else if (width >= 1366) {
      // 768p屏幕
      minHeight = 600;
      maxHeight = 1100;
    } else if (width >= 1024) {
      // 平板横屏
      minHeight = 500;
      maxHeight = 900;
    } else {
      // 小屏幕
      minHeight = 400;
      maxHeight = 700;
    }
    
    // 确保在合理范围内
    return Math.max(minHeight, Math.min(baseHeight, maxHeight));
  }, [effectiveSize]);

  // 计算最佳缩放级别，在90%高度容器内最大化地图显示
  const calculateOptimalZoom = useCallback(() => {
    const { width } = effectiveSize;
    const containerHeight = calculateContainerHeight();
    
    // 中国地图的大致边界范围
    const chinaMapBounds = {
      width: 62,  // 经度跨度 (135-73)
      height: 36  // 纬度跨度 (54-18)
    };
    
    // 使用实际容器尺寸计算最佳缩放
    const containerRatio = width / containerHeight;
    const mapRatio = chinaMapBounds.width / chinaMapBounds.height; // 约1.72
    
    let zoom;
    if (containerRatio > mapRatio) {
      // 容器更宽，高度是限制因子
      zoom = containerHeight / (chinaMapBounds.height * 16); // 调整系数从15到12，让地图更大
      // console.info("容器缩放比例：",zoom)
    } else {
      // 容器更高，宽度是限制因子
      zoom = width / (chinaMapBounds.width * 12);
    }
    
    // 设置更宽松的缩放范围，允许地图更大
    return Math.max(0.8, Math.min(zoom, 3.0)); // 最大缩放从3.5提升到5.0
  }, [effectiveSize, calculateContainerHeight]);

  // 计算最佳中心点
  const calculateOptimalCenter = useCallback((): [number, number] => {
    const { width, height } = effectiveSize;
    const containerRatio = width / height;
    
    // 中国地理中心
    let centerLng = 104;
    let centerLat = 35;
    
    // 根据容器宽高比微调中心点
    if (containerRatio > 2.0) {
      // 超宽屏：稍微向东移动
      centerLng = 106;
    } else if (containerRatio < 1.2) {
      // 竖屏或接近方形：稍微向南移动
      centerLat = 33;
    }
    
    return [centerLng, centerLat];
  }, [effectiveSize]);

  // 计算布局尺寸，在90%高度容器内进一步最大化地图
  const calculateLayoutSize = useCallback(() => {
    const { width } = effectiveSize;
    
    // 由于容器已经是屏幕高度的90%，可以更激进地使用空间
    if (width < 480) return '99%';      // 小手机：几乎全屏
    if (width < 768) return '98%';      // 大手机：最大化利用
    if (width < 1024) return '96%';     // 平板：留极少边距
    if (width < 1366) return '95%';     // 小笔记本：留少量边距
    if (width < 1920) return '94%';     // 大笔记本：适中边距
    return '92%';                       // 大屏幕：仍然很大
  }, [effectiveSize]);

  // 计算布局中心点
  const calculateLayoutCenter = useCallback((): [string, string] => {
    const { width, height } = effectiveSize;
    const containerRatio = width / height;
    
    let centerX = '50%';
    let centerY = '50%';
    
    // 根据屏幕比例微调布局中心
    if (containerRatio < 1.2) {
      // 竖屏：稍微上移
      centerY = '48%';
    } else if (containerRatio > 2.0) {
      // 超宽屏：保持居中
      centerX = '50%';
      centerY = '50%';
    }
    
    return [centerX, centerY];
  }, [effectiveSize]);

  // 计算响应式字体大小
  const calculateFontSize = useCallback(() => {
    const { width } = effectiveSize;
    
    if (width >= 2560) return 14;      // 4K屏幕
    if (width >= 1920) return 12;      // 1080p屏幕
    if (width >= 1680) return 11;      // 1050p屏幕
    if (width >= 1366) return 10;      // 768p屏幕
    if (width >= 1024) return 9;       // 平板横屏
    return 8; // 小屏幕
  }, [effectiveSize]);

  // 计算响应式边框宽度
  const calculateBorderWidth = useCallback(() => {
    const { width } = effectiveSize;
    
    if (width >= 2560) return 1.5;    // 4K屏幕
    if (width >= 1920) return 1;      // 1080p屏幕
    if (width >= 1680) return 1;      // 1050p屏幕
    if (width >= 1366) return 0.8;    // 768p屏幕
    if (width >= 1024) return 0.5;    // 平板横屏
    return 0.5; // 小屏幕
  }, [effectiveSize]);

  // 监听窗口大小变化（仅当没有容器尺寸时）
  useEffect(() => {
    if (typeof window === 'undefined' || (containerSize && containerSize.width > 0)) return;

    const handleResize = debounce(() => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }, 300);

    window.addEventListener('resize', handleResize);
    
    // 初始化时也触发一次
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [containerSize]);

  // 返回响应式配置
  return {
    containerHeight: calculateContainerHeight(),
    zoom: calculateOptimalZoom(),
    center: calculateOptimalCenter(),
    layoutSize: calculateLayoutSize(),
    layoutCenter: calculateLayoutCenter(),
    fontSize: calculateFontSize(),
    borderWidth: calculateBorderWidth()
  };
};
